import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography } from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  FileDownload as ExportIcon,
  InfoOutlined,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";
import Q8Button from "../components/Q8Button";

const AccountsConfigurationReadOnly = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [filtersActive, setFiltersActive] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
    title: "",
  });

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type, title: "" });
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getAccountsConfiguration();

      // Filtra solo i record con contoTech = true
      const filteredData = data.filter((item) => {
        // Assicurati che contoTech sia interpretato come booleano
        const isContoTech =
          typeof item.contoTech === "string"
            ? item.contoTech === "true"
            : Boolean(item.contoTech);

        return isContoTech;
      });

      if (table) {
        table.setData(filteredData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table?.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Conti Tech Data");
      XLSX.writeFile(wb, "Conti_Tech_data.xlsx");
    }
  };

  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }

      const columns = [
        {
          title: "Conto",
          field: "conto",
          widthGrow: 1,
          headerFilter: "input",
        },
        {
          title: "Descrizione",
          field: "desc_IT",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "Livello 1 OPEX",
          field: "level1Opex",
          widthGrow: 2,
          headerFilter: "input",
        },
        {
          title: "Conto Tech",
          field: "contoTech",
          widthGrow: 1,
          formatter: "tickCross",
          formatterParams: {
            allowEmpty: false,
            tickElement: "<span style='color:green'>✓</span>",
            crossElement: "<span style='color:red'>✗</span>",
          },
          headerFilter: "list",
          headerFilterParams: {
            values: { "": "Tutti", true: "Sì", false: "No" },
          },
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "conto", dir: "asc" }],
        rowHeight: 40,
        selectable: false, // Non è necessaria la selezione
        responsiveLayout: false,
        autoResize: false,
        clipboard: true,
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Conti Tech", href: "/accounts-tech" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Conti Tech
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi filtri" : "Mostra filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);

                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          mb: 2,
          bgcolor: "#E3F2FD", // Colore blu più chiaro per differenziare dalla pagina di gestione
          border: "1px solid #90CAF9",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#1976D2" }} />
        <Typography variant="body2">
          Visualizzazione di sola lettura dei Conti Tech. Questa tabella mostra
          solo i conti con attributo "Conto Tech" attivo.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Q8SnackBar
        title={snackbar.title}
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AccountsConfigurationReadOnly;
